import React from 'react';
import {
  View,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ViewStyle,
  StyleProp,
} from 'react-native';
import { Text } from '@/components/common/text';
import Icon from 'react-native-vector-icons/Ionicons';
import { CommunityResource } from '@/types/residents/communityResource';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { ResourceIcon as DefaultResourceIcon } from '@/components/residents/resources/ResourceIcon';
import { GlassCard } from '../GlassCard';
import { useThemeColors } from '@/hooks/shared/useThemeColors';

export interface ResourceGlassCardProps {
  resource?: CommunityResource;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  iconName?: string;
  iconBg?: string;
  onPress: (resource?: CommunityResource) => void;
  linkText?: string;
  style?: StyleProp<ViewStyle>;
  descriptionLines?: number;

  editMode?: boolean;
  onEdit?: (resource?: CommunityResource) => void;
  showEditButton?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (resource?: CommunityResource) => void;
  secondaryMeta?: string;
  iconRenderer?: (iconKey?: string) => React.ReactNode;
  minHeight?: number;
  avatarSize?: number;
  bodyPressable?: boolean;
}

const hexToRgba = (hex: string, alpha: number): string => {
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const ResourceGlassCard: React.FC<ResourceGlassCardProps> = React.memo(({
  resource,
  title: customTitle,
  description: customDescription,
  icon: customIcon,
  iconName,
  iconBg,
  onPress,
  linkText = 'See details',
  style,
  descriptionLines = 2,
  editMode = false,
  onEdit,
  showEditButton,
  isSelected = false,
  onToggleSelect,
  secondaryMeta,
  iconRenderer,
  minHeight,
  avatarSize = 48,
  bodyPressable = false,
}) => {
  const { colors, semantic } = useThemeColors();
  const resolvedIconBg = iconBg || colors.accent.blueBgLight;
  const title = customTitle || resource?.name || resource?.title || '';
  const description = customDescription || resource?.description || '';

  const renderIcon = () => {
    if (customIcon) return customIcon;
    if (iconName) {
      return <Icon name={iconName} size={22} color={colors.text.primary} />;
    }
    if (iconRenderer && resource) {
      return iconRenderer(resource.icon || resource.iconName);
    }
    if (resource) {
      return (
        <DefaultResourceIcon
          iconKey={resource.icon || resource.iconName}
          size={22}
          color={colors.text.primary}
        />
      );
    }
    return null;
  };

  const avatarStyle = {
    width: avatarSize,
    height: avatarSize,
    borderRadius: 14,
    backgroundColor: resolvedIconBg,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
  };

  // ======================================================================
  // EDIT MODE — admin Resources screen layout (header + body + footer buttons)
  // ======================================================================
  if (editMode) {
    const showEdit = (showEditButton ?? true) && !!onEdit;
    const moveLabel = isSelected ? 'Cancel' : 'Move';
    const moveButtonStyle: ViewStyle = isSelected
      ? {
          backgroundColor: hexToRgba(semantic.status.error, 0.15),
          borderColor: semantic.status.error,
          borderWidth: 1,
        }
      : {
          backgroundColor: 'transparent',
          borderColor: colors.glass.buttonBorder,
          borderWidth: 1,
        };
    const moveTextColor = isSelected ? semantic.status.error : colors.text.primary;

    const handleMovePress = () => {
      onToggleSelect?.(resource);
    };
    const handleEditPress = () => {
      onEdit?.(resource);
    };
    const handleBodyPress = () => {
      onPress(resource);
    };

    const cardInner = (
      <GlassCard
        borderRadius={REDESIGN_SPACING.quickActionRadius}
        padding={REDESIGN_SPACING.cardPadding}
        style={[
          styles.editCard,
          minHeight !== undefined && { flex: 1, minHeight },
          isSelected && { borderColor: colors.accent.blueBg },
        ]}
      >
        <View style={styles.editHeader}>
          <View style={avatarStyle}>{renderIcon()}</View>
          <View style={styles.editHeaderText}>
            <Text
              style={[styles.title, { color: colors.text.primary }]}
              numberOfLines={2}
            >
              {title}
            </Text>
            {secondaryMeta ? (
              <Text
                style={[styles.meta, { color: colors.text.subtle }]}
                numberOfLines={1}
              >
                {secondaryMeta}
              </Text>
            ) : null}
          </View>
        </View>

        {description ? (
          <Text
            style={[styles.description, { color: colors.text.muted }]}
            numberOfLines={descriptionLines}
          >
            {description}
          </Text>
        ) : null}

        <View style={styles.editFooter}>
          <TouchableOpacity
            onPress={handleMovePress}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={`${moveLabel} ${title}`}
            style={[styles.editFooterButton, moveButtonStyle]}
          >
            <Text style={[styles.editFooterButtonText, { color: moveTextColor }]}>
              {moveLabel}
            </Text>
          </TouchableOpacity>
          {showEdit ? (
            <TouchableOpacity
              onPress={handleEditPress}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel={`Edit ${title}`}
              style={[
                styles.editFooterButton,
                {
                  backgroundColor: PRIMITIVE_COLORS.resioBlue,
                  borderColor: PRIMITIVE_COLORS.resioBlue,
                  borderWidth: 1,
                },
              ]}
            >
              <Text style={[styles.editFooterButtonText, { color: '#FFFFFF' }]}>
                Edit
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </GlassCard>
    );

    if (bodyPressable) {
      return (
        <Pressable
          onPress={handleBodyPress}
          accessibilityRole="button"
          accessibilityLabel={`${title}. ${description}`}
          accessibilityState={{ selected: isSelected }}
          style={style}
        >
          {cardInner}
        </Pressable>
      );
    }

    return <View style={style}>{cardInner}</View>;
  }

  // ======================================================================
  // VIEW MODE — residents layout (avatar + content + bottom link).
  // Preserved verbatim from the previous implementation; do not change.
  // ======================================================================
  const handlePress = () => {
    onPress(resource);
  };

  return (
    <TouchableOpacity
      onPress={handlePress}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={`${title}. ${description}`}
      accessibilityState={{ selected: isSelected }}
      style={style}
    >
      <GlassCard
        borderRadius={REDESIGN_SPACING.quickActionRadius}
        padding={REDESIGN_SPACING.cardPadding}
        style={[
          styles.viewCard,
          minHeight !== undefined && { flex: 1, minHeight },
          isSelected && { borderColor: colors.accent.blueBg },
        ]}
      >
        <View style={avatarStyle}>{renderIcon()}</View>

        <View style={styles.viewContent}>
          <View style={styles.textSection}>
            <Text style={[styles.title, { color: colors.text.primary }]} numberOfLines={1}>
              {title}
            </Text>
            {description ? (
              <Text
                style={[styles.description, { color: colors.text.muted }]}
                numberOfLines={descriptionLines}
              >
                {description}
              </Text>
            ) : null}
            {secondaryMeta ? (
              <Text style={[styles.meta, { color: colors.text.subtle }]} numberOfLines={1}>
                {secondaryMeta}
              </Text>
            ) : null}
          </View>

          <View style={styles.linkRow}>
            <Text style={styles.linkText}>{linkText}</Text>
            <Icon name="chevron-forward" size={16} color={PRIMITIVE_COLORS.resioBlue} />
          </View>
        </View>
      </GlassCard>
    </TouchableOpacity>
  );
});

ResourceGlassCard.displayName = 'ResourceGlassCard';

const styles = StyleSheet.create({
  // Edit-mode (admin) styles
  editCard: {
    flexDirection: 'column',
    gap: 10,
  },
  editHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  editHeaderText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  editFooter: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 'auto',
    paddingTop: 4,
  },
  editFooterButton: {
    flex: 1,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  editFooterButtonText: {
    fontSize: 13,
    fontWeight: '600',
  },

  // View-mode (residents) styles — unchanged from previous version
  viewCard: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  viewContent: {
    flex: 1,
    gap: 10,
  },
  textSection: {
    gap: 3,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
  },
  description: {
    ...REDESIGN_TYPOGRAPHY.small,
  },
  meta: {
    ...REDESIGN_TYPOGRAPHY.caption,
    marginTop: 2,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  linkText: {
    ...REDESIGN_TYPOGRAPHY.small,
    color: PRIMITIVE_COLORS.resioBlue,
  },
});
